import { createContext, useContext, useEffect, useState } from 'react';

const CartContext = createContext([]);

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    const stored = localStorage.getItem('cart');
    return stored ? JSON.parse(stored) : [];
  });

  const [cartId, setCartId] = useState(() => {
    return localStorage.getItem('cartId') || null;
  });

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cartItems));
  }, [cartItems]);

  useEffect(() => {
    if (cartId) {
      localStorage.setItem('cartId', cartId);
    } else {
      localStorage.removeItem('cartId');
    }
  }, [cartId]);

  useEffect(() => {
    if (cartItems.length === 0 && cartId) {
      setCartId(null);
    }
  }, [cartItems, cartId]);

  const setCart = (cartItems, id = null) => {
    if (Array.isArray(cartItems)) {
      setCartItems(
        cartItems.map((item) => ({
          merchantConfigId: item.merchantConfigId,
          productId: item.productId,
          variantId: item.variantId,
          enName: item.enName,
          frName: item.frName,
          enVariantName: item.enVariantName || null,
          frVariantName: item.frVariantName || null,
          variantType: item.variantType || null,
          variantView: item.variantView || null,
          variantEnTitle: item.variantEnTitle || null,
          variantFrTitle: item.variantFrTitle || null,
          price: item.price,
          tax: item.tax,
          images: item.images,
          quantity: item.quantity,
          minQuantity: item.minQuantity || 1,
          maxQuantity: item.maxQuantity,
        })),
      );
    }
    if (id) {setCartId(id);}
  };

  const updateQuantity = (productId, merchantConfigId, variantId, quantity) => {
    if (quantity <= 0) {
      removeItem(productId, merchantConfigId, variantId);
      return;
    }

    setCartItems((prev) =>
      prev.map((i) => {
        const matchesProduct = i.productId === productId && i.merchantConfigId === merchantConfigId;
        const matchesVariant = variantId != null ? i.variantId === variantId : true; // important: do not change != for !==

        return matchesProduct && matchesVariant ? { ...i, quantity } : i;
      }),
    );
  };

  const removeItem = (productId, merchantConfigId, variantId) => {
    setCartItems((prev) =>
      prev.filter((i) =>
        variantId != null
          ? !(i.productId === productId && i.merchantConfigId === merchantConfigId && i.variantId === variantId)
          : !(i.productId === productId && i.merchantConfigId === merchantConfigId),
      ),
    );
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  const totalPrice = cartItems.reduce(
    (sum, item) => sum + item.quantity * (item.price || 0),
    0,
  );

  const uniqueItems = Array.isArray(cartItems) ? cartItems.length : 0;

  return (
    <CartContext.Provider
      value={{
        cartItems,
        updateQuantity,
        removeItem,
        clearCart,
        totalItems,
        totalPrice,
        uniqueItems,
        setCart,
        cartId,
        setCartId,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
